/**
 * The encounter director (plan M2-13, spec §9.2-§9.3): a pure, seeded step function that decides **when** a
 * raider group or the repo crew should appear. It never touches the sim: the encounters runtime (M2-15) calls
 * `stepDirector` once per tick in the `end` stage, turns a request into ships with `composition` and places
 * them with `spawnPositions` (`./placement`).
 *
 * - **Raiders:** a roll every `intervalSeconds` [min, max] of game time in space. The roll succeeds with
 *   `min(maxChance, baseChance + cargoValue / cargoValueScale)`; the group is the M1 wave of the highest cargo
 *   tier whose `minCargoValue` ≤ the cargo value. A raid on a hull of a band in `bandScale` (M10 P4b6a: an L) draws
 *   the wave `tierStep` tiers higher, and the first pack ship its kit fits flies the kit (the Harpoon; `RaidTarget`).
 * - **Repo crew:** while collections is `repo` and not paused, the crew is due `undockDelaySeconds` after an
 *   undock, or at a seeded time within `inSpaceWithinSeconds` when collections turns `repo` while in space.
 *   One crew per sortie: after it is sent, the next one waits for the next undock (or a pause or a payment
 *   resetting collections).
 * - **Exclusions:** nothing spawns while auto-docking or within `stationExclusion` of a station's hull (`nearStation` is
 *   the hull distance, scale SC-5e1); a due request
 *   simply waits and retries on the next call. At most one request per call; the repo crew goes first.
 *
 * Allocation-free per call (D-014): state and request are preallocated and mutated in place.
 */
import { nextFloat, range, type Rng } from '../core/rng'
import type { SpawnRequests } from '../ai/waves'
import type { ShipBand, WavesData } from '../data/schemas'
import type { EncountersConfig } from '../data/m2Schemas'
import { COLLECTIONS_CODE, type EncounterDeltas } from '../space/contracts'
import { ENCOUNTER_KIND } from '../space/events'

/** `EncounterRequest.kind` when nothing is requested. */
export const REQUEST_NONE = -1

export interface EncounterDirectorState {
  /** Game time of the next raider roll; ≤ 0 = unscheduled (the next call schedules it). */
  nextAt: number
  /** Game time the repo crew is due, or -1. */
  repoDueAt: number
  /** Last group id handed out (ids start at 1). */
  groupSeq: number
  /**
   * Game time the last repo crew was sent this sortie, or -1. Not persisted (`EncounterDeltas` has no field
   * for it): after a load in space with collections at `repo`, the crew is rescheduled within the in-space window.
   */
  repoSentAt: number
}

export function createDirectorState(): EncounterDirectorState {
  return { nextAt: 0, repoDueAt: -1, groupSeq: 0, repoSentAt: -1 }
}

/** Restores the persisted timers (`EncounterDeltas`) into `state`. */
export function importDirectorState(state: EncounterDirectorState, deltas: EncounterDeltas): void {
  state.nextAt = deltas.nextEncounterAt
  state.repoDueAt = deltas.repoDueAt
  state.groupSeq = deltas.groupSeq
  state.repoSentAt = -1
}

/** Writes the persisted timers of `state` into `deltas` (groups are the runtime's business). */
export function exportDirectorState(state: EncounterDirectorState, deltas: EncounterDeltas): void {
  deltas.nextEncounterAt = state.nextAt
  deltas.repoDueAt = state.repoDueAt
  deltas.groupSeq = state.groupSeq
}

/** What the runtime tells the director each call (plain object, mutated in place by the caller). */
export interface DirectorInput {
  /** Game time (s). */
  now: number
  /** The player is flying (not docked, not dead, not loading). */
  inSpace: boolean
  /** Auto-dock is running: no spawns. */
  autodocking: boolean
  /** `CareerMirror.cargoValue` (credits). */
  cargoValue: number
  /** `CareerMirror.collections` (`COLLECTIONS_CODE`). */
  collections: number
  /** `CareerMirror.repoPaused`. */
  repoPaused: boolean
  /** Game time of the last undock, or -1 (never undocked, e.g. a new career that starts in space). */
  undockedAt: number
  /** Distance from the player to the nearest station's hull (m; `|p − centre| − R`, scale SC-5e1); `Infinity` when there is none. */
  nearStation: number
}

export function createDirectorInput(): DirectorInput {
  return {
    now: 0,
    inSpace: false,
    autodocking: false,
    cargoValue: 0,
    collections: COLLECTIONS_CODE.none,
    repoPaused: false,
    undockedAt: -1,
    nearStation: Number.POSITIVE_INFINITY,
  }
}

/** The director's output for one call. */
export interface EncounterRequest {
  /** `ENCOUNTER_KIND` value, or `REQUEST_NONE`. */
  kind: number
  /** Group id handed out for this request (≥ 1), or 0. */
  group: number
  /** Index into `GameData.waves.waves` (raiders and contract groups); -1 for the repo crew. */
  waveIndex: number
  /** Raiders: cargo tier index into `encounters.json tiers`. Contract: index into `contractTiers`. Repo: -1. */
  tier: number
}

export function createEncounterRequest(): EncounterRequest {
  return { kind: REQUEST_NONE, group: 0, waveIndex: -1, tier: -1 }
}

/** Raider roll chance for a hold worth `cargoValue`. */
export function raiderChance(cfg: EncountersConfig, cargoValue: number): number {
  return Math.min(cfg.maxChance, cfg.baseChance + Math.max(0, cargoValue) / cfg.cargoValueScale)
}

/** Index into `cfg.tiers` of the highest tier whose `minCargoValue` ≤ `cargoValue`. */
export function cargoTier(cfg: EncountersConfig, cargoValue: number): number {
  const tiers = cfg.tiers
  let t = 0
  for (let i = 1; i < tiers.length; i++) {
    if (cargoValue >= tiers[i].minCargoValue) t = i
  }
  return t
}

function clearRequest(out: EncounterRequest): void {
  out.kind = REQUEST_NONE
  out.group = 0
  out.waveIndex = -1
  out.tier = -1
}

/**
 * One director step. Clears `out`, then fills it with at most one request. Returns the requested kind or
 * `REQUEST_NONE`. Uses `rng` only for interval draws, raider rolls and the in-space repo delay, so two
 * directors fed the same inputs and seed produce the same requests.
 */
export function stepDirector(
  state: EncounterDirectorState,
  input: DirectorInput,
  cfg: EncountersConfig,
  rng: Rng,
  out: EncounterRequest,
): number {
  clearRequest(out)
  const now = input.now

  // Repo bookkeeping runs even while docked, so a payment or a pause cancels a pending crew.
  if (input.collections !== COLLECTIONS_CODE.repo || input.repoPaused) {
    state.repoDueAt = -1
    state.repoSentAt = -1
  } else if (state.repoSentAt >= 0 && input.undockedAt > state.repoSentAt) {
    // Another sortie since the last crew: the next one may come.
    state.repoSentAt = -1
  }
  if (!input.inSpace) return REQUEST_NONE

  if (input.collections === COLLECTIONS_CODE.repo && !input.repoPaused && state.repoSentAt < 0 && state.repoDueAt < 0) {
    const repo = cfg.repo
    if (input.undockedAt >= 0 && now - input.undockedAt <= repo.undockDelaySeconds) {
      state.repoDueAt = input.undockedAt + repo.undockDelaySeconds
    } else {
      state.repoDueAt = now + nextFloat(rng) * repo.inSpaceWithinSeconds
    }
  } else if (state.repoDueAt >= 0 && input.undockedAt >= 0 && state.repoDueAt < input.undockedAt + cfg.repo.undockDelaySeconds) {
    // Due while the player was docked: the crew waits for the undock delay like any other sortie.
    state.repoDueAt = input.undockedAt + cfg.repo.undockDelaySeconds
  }
  if (state.nextAt <= 0) state.nextAt = now + range(rng, cfg.intervalSeconds[0], cfg.intervalSeconds[1])

  const excluded = input.autodocking || input.nearStation < cfg.stationExclusion
  if (excluded) return REQUEST_NONE

  if (state.repoDueAt >= 0 && now >= state.repoDueAt) {
    state.repoDueAt = -1
    state.repoSentAt = now
    state.groupSeq++
    out.kind = ENCOUNTER_KIND.repo
    out.group = state.groupSeq
    return out.kind
  }

  if (now >= state.nextAt) {
    state.nextAt = now + range(rng, cfg.intervalSeconds[0], cfg.intervalSeconds[1])
    if (nextFloat(rng) < raiderChance(cfg, input.cargoValue)) {
      const tier = cargoTier(cfg, input.cargoValue)
      state.groupSeq++
      out.kind = ENCOUNTER_KIND.raiders
      out.group = state.groupSeq
      out.tier = tier
      out.waveIndex = cfg.tiers[tier].waveIndex
      return out.kind
    }
  }
  return REQUEST_NONE
}

/**
 * A clear-raiders contract group request for contract tier `tier` (clamped to `contractTiers`), with a fresh
 * group id. The runtime calls it when the player nears a pending contract marker.
 */
export function contractRequest(state: EncounterDirectorState, cfg: EncountersConfig, tier: number, out: EncounterRequest): number {
  const last = cfg.contractWaveIndex.length - 1
  const t = tier < 0 ? 0 : tier > last ? last : tier
  state.groupSeq++
  out.kind = ENCOUNTER_KIND.contract
  out.group = state.groupSeq
  out.tier = t
  out.waveIndex = cfg.contractWaveIndex[t]
  return out.kind
}

/**
 * A raid's target (M10 P4b6a; hull tiers spec §2.5, §4.5): the flown hull's size band, and the def slot a kit flies
 * on. The Reach raider hook supplies it; without one, raids are composed as before.
 */
export interface RaidTarget {
  readonly band: ShipBand
  /** The def slot of kit `kitId` on base def `defIndex`, or -1 when the kit does not fit that hull. Rare (a spawn). */
  kitSlot(defIndex: number, kitId: string): number
}

/**
 * The ships of `request`: writes `defIndex` and `profile` (M1 waves data for raiders and contract groups,
 * `encounters.json repo.ships` for the repo crew) into `out`, sets `out.count` and returns it. Positions and
 * rotations are left to `spawnPositions`. No escalation: M2 groups use the wave's base profiles. A raid on a `target`
 * whose band is in `cfg.bandScale` takes the higher tier's wave and one kitted ship (no ship added).
 */
export function composition(
  request: EncounterRequest,
  cfg: EncountersConfig,
  game: { waves: WavesData },
  out: SpawnRequests,
  target: RaidTarget | null = null,
): number {
  out.count = 0
  if (request.kind === ENCOUNTER_KIND.repo) {
    const ships = cfg.repo.ships
    const n = Math.min(ships.length, out.capacity)
    for (let k = 0; k < n; k++) {
      out.defIndex[k] = ships[k].defIndex
      out.profile[k] = ships[k].profileIndex
    }
    out.count = n
    return n
  }
  const scale = target !== null && request.kind === ENCOUNTER_KIND.raiders && request.tier >= 0 ? cfg.bandScale?.[target.band] : undefined
  const top = cfg.tiers.length - 1
  const waveIndex = scale === undefined ? request.waveIndex : cfg.tiers[Math.min(top, request.tier + (scale.tierStep | 0))].waveIndex
  if (request.kind === REQUEST_NONE || waveIndex < 0 || waveIndex >= game.waves.waves.length) return 0
  const ships = game.waves.waves[waveIndex].ships
  const n = Math.min(ships.length, out.capacity)
  for (let k = 0; k < n; k++) {
    out.defIndex[k] = ships[k].defIndex
    out.profile[k] = ships[k].profileIndex
  }
  out.count = n
  for (let k = 0; scale !== undefined && target !== null && k < n; k++) {
    const slot = target.kitSlot(out.defIndex[k], scale.kit)
    if (slot >= 0) {
      out.defIndex[k] = slot
      break
    }
  }
  return n
}
